import type { CSSProperties } from 'react';

/**
 * Shared style constants used across most generic blocks. Every value
 * reads from a CSS custom property (set by {@code ThemeTokens} at
 * request time) with a sensible fallback so blocks never render blank
 * when a tenant hasn't defined the token.
 *
 * Token → CSS variable mapping (from the W3C design-token tree):
 *   color.brand.500     → --color-brand-500
 *   font.size.sm        → --font-size-sm
 *   space.4             → --space-4
 *   radius.md           → --radius-md
 *   shadow.md           → --shadow-md
 *   app.max-width → --app-max-width
 */

// ---------------------------------------------------------------------------
// Layout
// ---------------------------------------------------------------------------

/**
 * Blocks migrated onto `@akademi/ui`'s `<Card>` spread one of these two
 * objects into the `style` of the `<Card>` (or its `asChild` child).
 *
 * `<Card>` carries layout + decoration through its own utility classes
 * (`flex flex-col gap-4 py-4 overflow-hidden bg-card text-card-foreground`
 * …). Inline `style` beats a class, so spreading `cardReset` cancels the
 * ones that would otherwise leak into a block that manages its own layout.
 * `overflow` / `boxShadow` are deliberately left out — cards with an
 * injected `.lms-*` stylesheet set those there and an inline value would
 * clobber them; such a card sets `overflow` inline itself when it needs to.
 *
 * `cardReset` — neutralize only; for a card that brings its own surface
 * (the `src/cards/*CourseCard` variants, the gradient `CtaCardModern`).
 * `cardStyle` — `cardReset` + the standard bordered card surface
 * (background + border + radius + shadow); spread by the generic bordered
 * blocks (FeatureGrid, NotificationsTimeline, LearningJourneys, the two
 * Activity blocks, the two Wiki blocks). Tune the shared card look here
 * once and every generic card follows.
 */
export const cardReset: CSSProperties = {
  display: 'block',
  flexDirection: 'row',
  gap: 0,
  padding: 0,
  overflow: 'visible',
  background: 'transparent',
  color: 'inherit',
};

export const cardStyle: CSSProperties = {
  ...cardReset,
  background: 'var(--color-surface-card, #fff)',
  border: '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
  borderRadius: 'var(--radius-md, 8px)',
};

export const sectionPad: CSSProperties = {
  padding: 'var(--space-16, 64px) var(--space-6, 24px)',
};

// `--sb-max-width-1200` (defined in portal.css) resolves to
// `var(--app-max-width, 1200px)` above 1200px viewport width, and to
// `100%` at or below it — so every consumer of `sectionInner` always
// fills its container on tablet/mobile regardless of what a tenant's
// theme sets `--app-max-width` to, without needing its own media query.
export const sectionInner: CSSProperties = {
  maxWidth: 'var(--sb-max-width-1200, var(--app-max-width, 1200px))',
  width: '100%',
  margin: '0 auto',
};

// ---------------------------------------------------------------------------
// Typography
// ---------------------------------------------------------------------------

export const muted: CSSProperties = {
  color: 'var(--color-text-secondary, #52525b)',
  fontSize: 'var(--font-size-sm, 14px)',
};

export const sectionTitle: CSSProperties = {
  margin: 0,
  fontFamily: 'var(--font-family-display, inherit)',
  fontSize: 'var(--font-size-3xl, clamp(30px, 4vw, 50px))',
  lineHeight: 'var(--font-leading-tight, 1.1)',
  letterSpacing: 0,
  fontWeight: 'var(--font-weight-semibold, 600)' as CSSProperties['fontWeight'],
  color: 'var(--color-text-primary, #18181b)',
};

/** Compact label that sits above section headings. */
export const kickerStyle: CSSProperties = {
  fontFamily: 'var(--font-family-body, inherit)',
  fontSize: 'var(--font-size-xs, 12px)',
  letterSpacing: 0,
  textTransform: 'uppercase',
  fontWeight: 700,
  color: 'var(--color-text-secondary, #71717a)',
};

export const textPrimary: CSSProperties = {
  color: 'var(--color-text-primary, #18181b)',
};

export const textSecondary: CSSProperties = {
  color: 'var(--color-text-secondary, #52525b)',
};

export const textTertiary: CSSProperties = {
  color: 'var(--color-text-tertiary, #71717a)',
};

/** White text — used on top of brand-colored or dark backgrounds. */
export const textOnBrand: CSSProperties = {
  color: 'var(--color-text-on-brand, #fff)',
};

/** White text on any dark/overlay background (not necessarily brand-colored). */
export const textInverse: CSSProperties = {
  color: 'var(--color-text-inverse, #fafafa)',
};

// ---------------------------------------------------------------------------
// Buttons
// ---------------------------------------------------------------------------

export const btnBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 'var(--space-2, 8px)',
  padding: 'var(--space-3, 12px) var(--space-5, 20px)',
  borderRadius: 'var(--radius-md, 8px)',
  fontFamily: 'var(--font-family-body, inherit)',
  fontSize: 'var(--font-size-sm, 14px)',
  fontWeight: 600,
  cursor: 'pointer',
  border: 0,
  textDecoration: 'none',
  transition: 'transform 150ms ease, box-shadow 150ms ease, background 150ms ease',
};

export const btnPrimary: CSSProperties = {
  ...btnBase,
  background: 'var(--color-brand-500, #319db3)',
  color: 'var(--color-text-on-brand, #fff)',
  boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.10))',
};

export const btnSecondary: CSSProperties = {
  ...btnBase,
  background: 'var(--color-surface-elevated, #fafafa)',
  color: 'var(--color-text-primary, #18181b)',
  border: '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
};

export const btnOutline: CSSProperties = {
  ...btnBase,
  background: 'transparent',
  color: 'var(--color-brand-600, #287f96)',
  border: '1px solid var(--color-brand-500, #319db3)',
};

/** Inverted button — white bg, dark text (e.g. on brand-colored hero). */
export const btnInverse: CSSProperties = {
  ...btnBase,
  background: 'var(--color-neutral-0, #fff)',
  color: 'var(--color-text-primary, #18181b)',
};

// ---------------------------------------------------------------------------
// Badges / chips
// ---------------------------------------------------------------------------

export const badgeBase: CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 'var(--space-1, 4px)',
  padding: '4px var(--space-3, 12px)',
  borderRadius: 'var(--radius-full, 999px)',
  fontSize: 'var(--font-size-xs, 12px)',
  fontWeight: 600,
};

export const badgeBrand: CSSProperties = {
  ...badgeBase,
  background: 'var(--color-brand-500, #319db3)',
  color: 'var(--color-text-on-brand, #fff)',
};

export const badgeSuccess: CSSProperties = {
  ...badgeBase,
  background: 'var(--color-success-500, #10b981)',
  color: 'var(--color-text-on-brand, #fff)',
};

export const badgeAmber: CSSProperties = {
  ...badgeBase,
  background: 'var(--color-amber-500, #f59e0b)',
  color: 'var(--color-text-on-brand, #fff)',
};

export const badgeError: CSSProperties = {
  ...badgeBase,
  background: 'var(--color-error-500, #ef4444)',
  color: 'var(--color-text-on-brand, #fff)',
};

// ---------------------------------------------------------------------------
// Surfaces
// ---------------------------------------------------------------------------

export const surfaceCard: CSSProperties = {
  background: 'var(--color-surface-card, #fff)',
  border: '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
  borderRadius: 'var(--radius-md, 8px)',
};

export const surfaceElevated: CSSProperties = {
  background: 'var(--color-surface-elevated, #fafafa)',
  boxShadow: 'var(--shadow-sm, 0 1px 3px rgba(0,0,0,0.10))',
  borderRadius: 'var(--radius-md, 8px)',
};

export const surfaceMuted: CSSProperties = {
  background: 'var(--color-surface-muted, #f4f4f5)',
  borderRadius: 'var(--radius-md, 8px)',
};

/** Fully dark/neutral background (e.g. video player canvas). */
export const surfaceDark: CSSProperties = {
  background: 'var(--color-neutral-950, #0a0a0a)',
};

// ---------------------------------------------------------------------------
// Misc
// ---------------------------------------------------------------------------

export const placeholderImg = (h = 140): CSSProperties => ({
  height: h,
  background:
    'linear-gradient(135deg, var(--color-neutral-100, #f4f4f5), var(--color-neutral-200, #e4e4e7))',
  borderRadius: 'var(--radius-sm, 4px)',
});

export const divider: CSSProperties = {
  border: 0,
  borderTop: '1px solid var(--color-border-default, rgba(0,0,0,0.12))',
};
